import { useState, useEffect, useCallback } from 'react';
import { authHeaders, Toast } from './listPageStyles';
import { usePageMeta } from '../hooks/usePageMeta';
import { fetchBoolSetting, SETTING_SHOW_PASSIVE_PRODUCTS } from './invoiceShared';
import {
  fetchNumberSetting, fetchTextSetting, fetchMultiSetting,
  SETTING_SESSION_IDLE_MINUTES, SETTING_CUSTOMER_KVKK_ENABLED,
  SETTING_SINGLE_SESSION, SETTING_ACCESS_TOKEN_MINUTES, SETTING_REFRESH_TOKEN_DAYS,
  SETTING_STRICT_AUTHORIZATION,
  SETTING_PASSWORD_POLICY_LEVEL, SETTING_PASSWORD_EXPIRY_DAYS, SETTING_PASSWORD_HISTORY_COUNT,
  SETTING_MAX_FAILED_ATTEMPTS, SETTING_LOCKOUT_MINUTES, SETTING_CAPTCHA_AFTER_ATTEMPTS,
  SETTING_ALLOW_LOCAL_PASSWORD_LOGIN, SETTING_TWO_FACTOR_MODE, SETTING_TWO_FACTOR_CHANNEL,
  SETTING_LOGIN_WINDOW_ENABLED, SETTING_LOGIN_WINDOW_CRITERIA_TYPE, SETTING_LOGIN_WINDOW_CRITERIA,
  SETTING_LOGIN_WINDOW_DAYS, SETTING_LOGIN_WINDOW_START, SETTING_LOGIN_WINDOW_END,
  SETTING_LOGIN_WINDOW_TIMEZONE,
  SETTING_INACTIVE_USER_DAYS,
  SETTING_MIN_ANDROID_API_LEVEL, SETTING_MIN_APP_VERSION_IOS, SETTING_BLOCK_ON_OLD_VERSION,
  SETTING_SUPPORT_WORKING_DAYS, SETTING_SUPPORT_HOURS_START, SETTING_SUPPORT_HOURS_END,
  SETTING_SUPPORT_TIMEZONE, SETTING_SUPPORT_AGENT_USER_IDS,
  SETTING_PRODUCT_AVAILABILITY_RETENTION_DAYS, SETTING_PRODUCT_AVAILABILITY_MAX_ROWS,
  SETTING_TEAM_CHAT_MESSAGE_RETENTION_DAYS, SETTING_TEAM_CHAT_VIOLATION_RETENTION_DAYS,
} from '../utils/settings';
import { DetailShell, Section } from './detail/DetailShell';
import { MultiChipSelect } from './campaign/MultiChipSelect';
import { RELATION_CONFIGS } from './relationConfigs';
import { usePageActions } from '../contexts/PermissionContext';
import './generalParameters.css';

// Genel parametreler. Setting tablosu (Key/Value/Group/Type) üzerinden upsert'lenir
// (POST /api/settings/SaveByKey). Yeni parametre eklemek için ilgili grubun items'ına satır ekle.
//
// Desteklenen tipler:
//   'bool'        anahtar            → değişince ANINDA kaydeder
//   'select'      tek seçim          → değişince ANINDA kaydeder
//   'number'      sayısal giriş      → min/max'a çekilir, Kaydet butonu
//   'text'        serbest metin      → Kaydet butonu
//   'time'        saat (HH:mm)       → Kaydet butonu
//   'multiselect' çoklu seçim        → DB'de virgülle ayrılmış tek string, Kaydet butonu
//
// 'multiselect' seçenekleri statik (options) olabileceği gibi sunucudan da gelebilir
// (optionsSource: 'users' | 'userGroups'). dependsOn verilirse seçenek kaynağı başka bir
// parametrenin değerine göre değişir — KK-16'daki Kod/Grup kriteri böyle çalışır.
//
// #817 `picker: true` verilen bir 'multiselect', onay-kutusu ızgarası yerine REHBERLİ seçimle
// (MultiChipSelect + RelationPicker) render edilir: aranabilir/sıralanabilir/sayfalı datagrid,
// seçilenler chip olarak, altında "Tümünü Seç / Temizle". Saklama biçimi DEĞİŞMEZ — değer yine
// virgülle ayrılmış id listesidir, dolayısıyla eski kayıtlar aynen okunur/yazılır.
//
/**
 * Android API seviyesi seçenekleri — sunucudaki AndroidVersionNames tablosunun ikizi.
 *
 * Değer SAYISAL saklanır (Build.VERSION.SDK_INT): cihazın bildirdiği sürüm metni ("15", "15.0",
 * bazen boş) üreticiye göre değiştiği için sıralanamaz, API seviyesi ise tartışmasızdır.
 * Liste 24'ten (Android 7.0) başlar — uygulamanın minSdk değeri bu; altındaki cihaz APK'yı
 * zaten kuramaz, o eşikleri sunmak hiç tetiklenmeyecek bir kural yazdırırdı.
 *
 * Boş seçenek "kontrol yok" demektir; sunucu 0/boş değeri kapıyı kapalı sayar.
 */
const ANDROID_API_OPTIONS = [
  { value: '', label: 'Kontrol yok' },
  { value: '24', label: 'Android 7.0 (API 24)' },
  { value: '25', label: 'Android 7.1 (API 25)' },
  { value: '26', label: 'Android 8.0 (API 26)' },
  { value: '27', label: 'Android 8.1 (API 27)' },
  { value: '28', label: 'Android 9 (API 28)' },
  { value: '29', label: 'Android 10 (API 29)' },
  { value: '30', label: 'Android 11 (API 30)' },
  { value: '31', label: 'Android 12 (API 31)' },
  { value: '32', label: 'Android 12L (API 32)' },
  { value: '33', label: 'Android 13 (API 33)' },
  { value: '34', label: 'Android 14 (API 34)' },
  { value: '35', label: 'Android 15 (API 35)' },
  { value: '36', label: 'Android 16 (API 36)' },
];

// Grup başlıkları ekranda ayrı kart (Section) olarak render edilir.
const GROUPS = [
  {
    name: 'Distribütör Genel Parametreleri',
    icon: 'box',
    subtitle: 'Sipariş, fatura ve irsaliye ekranlarının davranışını etkiler.',
    items: [
      {
        key: SETTING_SHOW_PASSIVE_PRODUCTS,
        type: 'bool',
        label: 'Pasif ürünleri kalem rehberinde göster',
        help: 'Kapalıyken sipariş/fatura/irsaliye kalem ürün seçiminde pasif (Status=Pasif) ürünler listelenmez.',
        default: false,
      },
    ],
  },
  {
    name: 'Müşteri Kartı',
    icon: 'user',
    subtitle: 'Müşteri tanımlama ekranının davranışını etkiler.',
    items: [
      {
        key: SETTING_CUSTOMER_KVKK_ENABLED,
        type: 'bool',
        label: 'KVKK onay süreci aktif',
        help: 'Açıkken yeni müşteri "Onaysız" oluşur ve hassas alanlar (TC, telefon, e-posta…) onay verilene kadar maskelenir. Kapalıyken müşteri "Onaylı" oluşur ve maskeleme uygulanmaz.',
        default: false,
      },
    ],
  },
  {
    name: 'Veri Saklama',
    icon: 'box',
    subtitle: 'Sınırsız büyüyen saha kayıtlarının ne kadar süre ham olarak tutulacağı.',
    items: [
      {
        key: SETTING_PRODUCT_AVAILABILITY_RETENTION_DAYS,
        type: 'number',
        label: 'Bulunurluk kaydı saklama süresi',
        help: 'Mobil, sipariş/fatura ekranından her çıkışta listelenen HER ürün için bir bulunurluk satırı yazar; tablo çok hızlı büyür. Gecelik iş, bu süreden eski satırları (müşteri, ürün, ay) bazında VAR/YOK sayacına indirger ve ham satırları SİLER — oran/trend korunur, gün ve ziyaret kırılımı kaybolur. Silme geri alınamaz. En az 30 gün uygulanır.',
        default: 180,
        min: 30,
        max: 3650,
        unit: 'gün',
      },
      {
        key: SETTING_PRODUCT_AVAILABILITY_MAX_ROWS,
        type: 'number',
        label: 'Bulunurluk arşivleme tur limiti',
        help: 'Gecelik işin tek turda arşivleyip sileceği azami satır sayısı. Birikmiş devasa bir tabloyu tek gecede silmeye çalışmak veritabanını kilitler; limit sayesinde tablo birkaç gecede hedefe iner. Değişiklik gerekmedikçe elleme.',
        default: 500000,
        min: 10000,
        max: 20000000,
        unit: 'satır',
      },
      // #44 Ekip mesajlaşması — İKİ AYRI süre. Bilinçli: mesaj sohbet metnidir, ihlal kaydı
      // denetim izidir ve daha uzun tutulur.
      {
        key: SETTING_TEAM_CHAT_MESSAGE_RETENTION_DAYS,
        type: 'number',
        label: 'Ekip mesajı saklama süresi',
        help: 'Ekip içi mesajlaşmadaki yazışmalar bu süreden sonra silinir. Sohbet ve katılımcı kayıtları KALIR (kim kiminle yazıştı izi korunur), yalnız mesaj metinleri gider ve liste önizlemeleri tazelenir. Silme geri alınamaz. En az 30 gün uygulanır.',
        default: 365,
        min: 30,
        max: 3650,
        unit: 'gün',
      },
      {
        key: SETTING_TEAM_CHAT_VIOLATION_RETENTION_DAYS,
        type: 'number',
        label: 'Yasaklı kelime ihlal kaydı saklama süresi',
        help: 'Yasaklı kelime yakalanan mesajların HAM metniyle birlikte tutulduğu denetim kayıtları bu süreden sonra silinir. Mesaj saklama süresinden UZUN tutulmalıdır: aynı süreye çekilirse ihlalin izi, hakkında bir işlem yapılmadan önce kaybolabilir. En az 90 gün uygulanır.',
        default: 730,
        min: 90,
        max: 3650,
        unit: 'gün',
      },
    ],
  },
  {
    name: 'Güvenlik / Oturum',
    icon: 'shield',
    subtitle: 'Oturum güvenliği ve otomatik zaman aşımı ayarları.',
    items: [
      {
        key: SETTING_SESSION_IDLE_MINUTES,
        type: 'number',
        label: 'Oturum hareketsizlik zaman aşımı',
        help: 'Back Office kullanıcısı bu süre kadar hiçbir işlem yapmazsa oturum güvenlik gereği otomatik sonlandırılır. En az 1 dakika.',
        default: 30,
        min: 1,
        max: 1440,
        unit: 'dakika',
      },
      {
        key: SETTING_SINGLE_SESSION,
        type: 'bool',
        label: 'Tek oturum kuralı',
        help: 'Açıkken bir kullanıcı aynı anda tek yerden giriş yapabilir; ikinci giriş öncekini düşürür. Destek tipi kullanıcılar bu kuraldan MUAFTIR — aynı hesabı birden fazla destek personeli kullanabilsin diye.',
        default: false,
      },
      {
        key: SETTING_ACCESS_TOKEN_MINUTES,
        type: 'number',
        label: 'Access token ömrü',
        help: 'Kısa ömürlü olması güvenlik gereğidir; süre dolduğunda istemci refresh token ile sessizce yeniler, kullanıcı yeniden giriş yapmaz.',
        default: 60,
        min: 5,
        max: 1440,
        unit: 'dakika',
      },
      {
        key: SETTING_REFRESH_TOKEN_DAYS,
        type: 'number',
        label: 'Refresh token ömrü',
        help: 'Bu süre boyunca hiç giriş yapmayan kullanıcı yeniden kimlik doğrulamak zorunda kalır. Saha kullanıcıları için kısa tutmak sık giriş demektir.',
        default: 30,
        min: 1,
        max: 365,
        unit: 'gün',
      },
      {
        key: SETTING_STRICT_AUTHORIZATION,
        type: 'bool',
        label: 'Katı yetki denetimi (fail-closed)',
        help:
          'KAPALIYKEN (varsayılan) yetki satırı tanımlanmamış menü ve roller AÇIK sayılır — bugünkü davranış. '
          + 'AÇILDIĞINDA yetkisi açıkça verilmemiş her ekran ve aksiyon KAPANIR. '
          + 'Açmadan önce sunucu günlüğündeki "AUTHZ-SHADOW / WOULD_DENY" kayıtlarına bakın: '
          + 'anahtar kapalıyken bile "açık olsaydı kapanacaktı" durumları oraya yazılır. '
          + 'Eksik rol yetkileri tamamlanmadan açılırsa kullanıcılar kullandıkları ekranlardan kilitlenir. '
          + 'Yönetici (ADMIN/SUPERADMIN) rolleri her iki durumda da muaftır.',
        default: false,
      },
    ],
  },
  {
    name: 'Şifre Politikası',
    icon: 'lock',
    subtitle: 'Şifre zorluğu, geçerlilik süresi ve başarısız giriş kilitlemesi.',
    items: [
      {
        key: SETTING_PASSWORD_POLICY_LEVEL,
        type: 'select',
        label: 'Şifre zorluk seviyesi',
        help: 'Tenant kartından buraya taşındı — tüm şifre kuralları tek ekranda toplansın diye. Seviye yükseldikçe uzunluk ve karakter çeşitliliği zorunluluğu artar.',
        default: '2',
        options: [
          { value: '1', label: '1 — En düşük' },
          { value: '2', label: '2 — Düşük' },
          { value: '3', label: '3 — Orta' },
          { value: '4', label: '4 — Yüksek' },
          { value: '5', label: '5 — En yüksek' },
        ],
      },
      {
        key: SETTING_PASSWORD_EXPIRY_DAYS,
        type: 'number',
        label: 'Şifre geçerlilik süresi',
        help: '0 = kapalı. Süre dolduğunda kullanıcı giriş ekranından şifresini değiştirmeye zorlanır. SSO ile giren kullanıcılara UYGULANMAZ — onların şifresi SSO sağlayıcısındadır.',
        default: 0,
        min: 0,
        max: 3650,
        unit: 'gün',
      },
      {
        key: SETTING_PASSWORD_HISTORY_COUNT,
        type: 'number',
        label: 'Tekrar edilemeyecek son şifre sayısı',
        help: 'Kullanıcı son N şifresinden birini yeniden seçemez. 0 = geçmiş kontrolü kapalı.',
        default: 3,
        min: 0,
        max: 24,
        unit: 'şifre',
      },
      {
        key: SETTING_MAX_FAILED_ATTEMPTS,
        type: 'number',
        label: 'Hesap kilitleme eşiği',
        help: '0 = kapalı. Ardışık bu kadar başarısız girişten sonra hesap geçici olarak kilitlenir. Başarılı girişte sayaç sıfırlanır.',
        default: 5,
        min: 0,
        max: 50,
        unit: 'deneme',
      },
      {
        key: SETTING_LOCKOUT_MINUTES,
        type: 'number',
        label: 'Kilit süresi',
        help: 'Eşik aşıldığında hesabın kilitli kalacağı süre. Yönetici kullanıcı kartından "Kilidi Aç" ile süreyi beklemeden açabilir.',
        default: 15,
        min: 1,
        max: 1440,
        unit: 'dakika',
      },
      {
        key: SETTING_CAPTCHA_AFTER_ATTEMPTS,
        type: 'number',
        label: 'Captcha eşiği',
        help: '0 = kapalı. Bu kadar başarısız denemeden sonra giriş ekranında captcha istenir. Kilitleme eşiğinden küçük olması mantıklıdır — önce captcha, sonra kilit.',
        default: 0,
        min: 0,
        max: 50,
        unit: 'deneme',
      },
    ],
  },
  {
    name: 'Kimlik Doğrulama',
    icon: 'shield',
    subtitle: 'SSO, yerel şifre girişi ve iki faktörlü doğrulama.',
    items: [
      {
        key: SETTING_ALLOW_LOCAL_PASSWORD_LOGIN,
        type: 'bool',
        label: 'Yerel şifre ile girişe izin ver',
        help: 'Kapalıyken giriş ekranında yalnız "SSO ile Giriş" görünür. SUPERADMIN bu kısıttan MUAFTIR — SSO sağlayıcısı erişilemez olduğunda sisteme girilebilmesi gerekir.',
        default: true,
      },
      {
        key: SETTING_TWO_FACTOR_MODE,
        type: 'select',
        label: 'İki faktörlü doğrulama',
        help: '"Opsiyonel" seçilirse kullanıcı kendi kartından açar/kapatır; "Zorunlu" seçilirse tüm kullanıcılar için istenir.',
        default: 'OFF',
        options: [
          { value: 'OFF', label: 'Kapalı' },
          { value: 'OPTIONAL', label: 'Opsiyonel' },
          { value: 'REQUIRED', label: 'Zorunlu' },
        ],
      },
      {
        key: SETTING_TWO_FACTOR_CHANNEL,
        type: 'select',
        label: 'Doğrulama kanalı',
        help: 'TOTP telefonda kurulan bir uygulama (Google/Microsoft Authenticator) kullanır ve internet gerektirmez; SMS ve e-posta operatör/sunucu bağımlıdır.',
        default: 'EMAIL',
        options: [
          { value: 'TOTP', label: 'TOTP (Authenticator)' },
          { value: 'SMS', label: 'SMS' },
          { value: 'EMAIL', label: 'E-posta' },
        ],
      },
    ],
  },
  {
    name: 'Giriş Gün ve Saat Kısıtı',
    icon: 'clock',
    subtitle: 'Belirlenen kullanıcıların yalnız izin verilen gün ve saatlerde giriş yapabilmesi.',
    items: [
      {
        key: SETTING_LOGIN_WINDOW_ENABLED,
        type: 'bool',
        label: 'Kısıt aktif',
        help: 'Kısıtın tek açma/kapama anahtarı budur. Kriter boş bırakılmış olması kısıtı kapatmaz — aksine HERKESE uygular.',
        default: false,
      },
      {
        key: SETTING_LOGIN_WINDOW_CRITERIA_TYPE,
        type: 'select',
        label: 'Kullanıcı kriter tipi',
        help: 'Kısıtın kimlere uygulanacağını hangi eksenden seçeceğinizi belirler. Kriter boş bırakılırsa bu seçim anlamsızdır — iki tip de aynı çalışır (herkese uygulanır).',
        default: 'CODE',
        options: [
          { value: 'CODE', label: 'Kod (kullanıcı seç)' },
          { value: 'GROUP', label: 'Grup (kullanıcı grubu seç)' },
        ],
      },
      {
        key: SETTING_LOGIN_WINDOW_CRITERIA,
        type: 'multiselect',
        picker: true,   // #817 rehberli seçim (arama + Tümünü Seç + Temizle)
        label: 'Kullanıcı kriteri',
        help: 'Rehberden seçin; kutuya tıklayıp arayabilir, "Tümünü Seç" ile hepsini işaretleyebilir, '
          + '"Temizle" ile seçimi boşaltabilirsiniz. BOŞ BIRAKILIRSA kısıt TÜM kullanıcılara uygulanır — '
          + 'boş bırakmak kısıtı KAPATMAZ. Dolu ise yalnız seçilenlere uygulanır, diğerleri kısıtsız girer. '
          + '"Tümünü Seç" ile boş bırakmak aynı şey DEĞİLDİR: seçim o anki kullanıcıları sabitler, '
          + 'sonradan açılan kullanıcılar kısıta girmez. Destek tipi kullanıcılar ve SUPERADMIN/ADMIN her durumda muaftır.',
        default: [],
        dependsOn: SETTING_LOGIN_WINDOW_CRITERIA_TYPE,
        optionsSource: (criteriaType) => (criteriaType === 'GROUP' ? 'userGroups' : 'users'),
      },
      {
        key: SETTING_LOGIN_WINDOW_DAYS,
        type: 'multiselect',
        // #841 Pencere artık KISIT penceresidir: seçilen gün + saat aralığında giriş KAPALIDIR.
        label: 'Girişe KAPALI günler',
        help: 'Seçilen günlerde, aşağıdaki saat aralığında giriş ENGELLENİR; bunun dışında giriş serbesttir. '
          + 'Hiçbir gün seçilmezse kısıt aktif olsa bile kimse engellenmez. '
          + 'Destek tipi kullanıcılar ve SUPERADMIN/ADMIN her durumda muaftır.',
        default: [],
        options: [
          { value: '1', label: 'Pzt' }, { value: '2', label: 'Sal' }, { value: '3', label: 'Çar' },
          { value: '4', label: 'Per' }, { value: '5', label: 'Cum' }, { value: '6', label: 'Cmt' },
          { value: '7', label: 'Paz' },
        ],
      },
      {
        key: SETTING_LOGIN_WINDOW_START,
        type: 'time',
        label: 'Başlangıç saati',
        help: 'Girişin ENGELLENDİĞİ aralığın başlangıcı. Aşağıdaki saat diliminde yorumlanır.',
        default: '07:00',
      },
      {
        key: SETTING_LOGIN_WINDOW_END,
        type: 'time',
        label: 'Bitiş saati',
        help: 'Girişin ENGELLENDİĞİ aralığın bitişi. Bitiş başlangıçtan küçükse aralık gece yarısını aşar (örn. 22:00–06:00).',
        default: '20:00',
      },
      {
        key: SETTING_LOGIN_WINDOW_TIMEZONE,
        type: 'text',
        label: 'Saat dilimi',
        help: 'Yukarıdaki saatlerin hangi saat diliminde yorumlanacağı. Sunucu UTC çalıştığı için '
          + 'bu ayar olmadan girilen saatler 3 saat kaymış uygulanır. Windows biçimi '
          + '("Turkey Standard Time") ve IANA biçimi ("Europe/Istanbul") kabul edilir; '
          + 'çözülemezse UTC+03 varsayılır.',
        default: 'Turkey Standard Time',
      },
    ],
  },
  {
    name: 'Kullanıcı Yaşam Döngüsü',
    icon: 'user',
    subtitle: 'Uzun süre kullanılmayan hesapların otomatik yönetimi.',
    items: [
      {
        key: SETTING_INACTIVE_USER_DAYS,
        type: 'number',
        label: 'Otomatik pasifleştirme süresi',
        help: '0 = kapalı. Bu kadar gündür hiç giriş yapmamış kullanıcılar zamanlanmış görevle Pasif duruma alınır. Pasifleştirme geri alınabilir; İptal DEĞİLDİR.',
        default: 0,
        min: 0,
        max: 3650,
        unit: 'gün',
      },
    ],
  },
  {
    name: 'Mobil / Cihaz',
    icon: 'box',
    subtitle: 'Saha uygulamasının minimum sürüm kontrolü.',
    items: [
      {
        // Ölçüt APK sürümü DEĞİL, cihazın Android sürümüdür (Build.VERSION.SDK_INT). Eski
        // "Minimum Android sürümü" alanı 1.4.0 gibi bir UYGULAMA sürümü bekliyordu ama etiketi
        // işletim sistemi sürümü gibi okunuyordu; kapının amacı (çok eski cihazı dışarıda
        // bırakmak) API seviyesiyle ölçülür. Değer sayısal saklanır, listede adıyla gösterilir:
        // "15"/"15.0" gibi cihaz metinleri üreticiye göre değişir, sıralanamaz.
        key: SETTING_MIN_ANDROID_API_LEVEL,
        type: 'select',
        label: 'Minimum Android sürümü (cihaz)',
        help: 'Cihazın Android sürümü bunun altındaysa aşağıdaki ayara göre uyarılır veya engellenir. '
          + 'Kullanıcı bunu uygulamayı güncelleyerek çözemez — mesaj donanım sağlayıcısına yönlendirir. '
          + 'Liste Android 7.0\'dan başlar: uygulamanın minSdk değeri 24, altındaki cihaz APK\'yı zaten kuramaz.',
        default: '',
        options: ANDROID_API_OPTIONS,
      },
      {
        key: SETTING_MIN_APP_VERSION_IOS,
        type: 'text',
        label: 'Minimum iOS sürümü',
        help: 'Boş = kontrol yok.',
        default: '',
        placeholder: 'örn. 1.4.0',
      },
      {
        key: SETTING_BLOCK_ON_OLD_VERSION,
        type: 'bool',
        label: 'Eski sürümde girişi engelle',
        help: 'Kapalıyken kullanıcı yalnız uyarılır ve çalışmaya devam eder; açıkken eski sürümle giriş tamamen reddedilir. Zorunlu bir şema değişikliği yaptıysanız açın.',
        default: false,
      },
    ],
  },
  {
    // #45 Destek sohbeti: AI ilk karşılamayı yapar, çözemediğinde talep canlı desteğe devredilir.
    // Bu ayarlar DEVRİ ENGELLEMEZ — mesai dışında talep yine açılır, kullanıcıya ne zaman
    // dönüleceği söylenir. Ayar hiç girilmezse sunucu hafta içi 09:00-18:00 (Türkiye) varsayar.
    name: 'Destek',
    icon: 'shield',
    subtitle: 'Mobil destek sohbetinin canlı desteğe devir davranışı ve mesai penceresi.',
    items: [
      {
        key: SETTING_SUPPORT_WORKING_DAYS,
        type: 'multiselect',
        label: 'Destek çalışma günleri',
        help: 'Kullanıcıya gösterilen mesai penceresini belirler. Hiçbir gün seçilmezse sunucu hafta içi varsayar.',
        default: ['1', '2', '3', '4', '5'],
        options: [
          { value: '1', label: 'Pzt' }, { value: '2', label: 'Sal' }, { value: '3', label: 'Çar' },
          { value: '4', label: 'Per' }, { value: '5', label: 'Cum' }, { value: '6', label: 'Cmt' },
          { value: '7', label: 'Paz' },
        ],
      },
      {
        key: SETTING_SUPPORT_HOURS_START,
        type: 'time',
        label: 'Mesai başlangıcı',
        help: 'Bu saatten önce gelen talepler "mesai dışı" olarak işaretlenir ve kullanıcıya dönüş saati bildirilir.',
        default: '09:00',
      },
      {
        key: SETTING_SUPPORT_HOURS_END,
        type: 'time',
        label: 'Mesai bitişi',
        help: 'Bu saatten sonra gelen talepler kuyrukta bekler; kullanıcı bilgilendirilir.',
        default: '18:00',
      },
      {
        key: SETTING_SUPPORT_TIMEZONE,
        type: 'text',
        label: 'Saat dilimi',
        help: 'Mesai saatlerinin yorumlandığı saat dilimi. Windows adı ("Turkey Standard Time") veya IANA adı ("Europe/Istanbul") kabul edilir. Çözülemezse UTC+03 kullanılır.',
        default: 'Turkey Standard Time',
        placeholder: 'Turkey Standard Time',
      },
      {
        key: SETTING_SUPPORT_AGENT_USER_IDS,
        type: 'multiselect',
        label: 'Bildirim alacak destek personeli',
        help: 'Yeni bir talep canlı desteğe devredildiğinde bildirim gönderilecek kullanıcılar. BOŞ BIRAKILIRSA bildirim gönderilmez — talep yine Destek Talepleri kuyruğunda görünür.',
        default: [],
        optionsSource: 'users',
      },
    ],
  },
];
const ALL_PARAMS = GROUPS.flatMap((g) => g.items.map((p) => ({ ...p, group: g.name })));
const paramByKey = Object.fromEntries(ALL_PARAMS.map((p) => [p.key, p]));

export function GeneralParametersPage() {
  usePageMeta('Genel Parametreler', 'Sistem Ayarları');
  const { canEdit } = usePageActions();
  const canSave = canEdit;
  const [values, setValues] = useState({});   // ekranda düzenlenen değerler
  const [saved, setSaved] = useState({});      // sunucuda kayıtlı son değerler (dirty tespiti)
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [toast, setToast] = useState(null);
  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3000); };

  // multiselect seçenek kaynakları — sunucudan bir kez çekilir, tip değişince yeniden istenmez.
  const [lookups, setLookups] = useState({ users: [], userGroups: [] });

  const load = useCallback(async () => {
    setLoading(true);
    const headers = authHeaders();
    const next = {};
    await Promise.all(ALL_PARAMS.map(async (p) => {
      if (p.type === 'bool') next[p.key] = await fetchBoolSetting(p.key, headers, p.default);
      else if (p.type === 'number') next[p.key] = await fetchNumberSetting(p.key, headers, p.default);
      else if (p.type === 'multiselect') next[p.key] = await fetchMultiSetting(p.key, headers, p.default);
      else next[p.key] = await fetchTextSetting(p.key, headers, p.default); // select | time | text
    }));
    setValues(next);
    setSaved(next);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Kullanıcı ve kullanıcı grubu listeleri — yalnız KK-16 kriter alanı için. Ekran açılışında
  // bir kez çekilir; hata halinde sessiz kalır (kriter alanı boş seçenekle açılır, ekran çökmez).
  //
  // #817 Satırlar HAM (OData) hâliyle saklanır: rehber (RelationPicker) kolon/arama/sıralama için
  // ham satırı ister, chip etiketi de aynı satırdan üretilir. Liste tek seferde çekilir çünkü
  // "Tümünü Seç" zaten tüm adayları bilmeyi gerektiriyor; $select ile yük minimumda tutulur.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const headers = authHeaders();
      const pull = async (url) => {
        try {
          const res = await fetch(url, { headers });
          if (!res.ok) return [];
          const j = await res.json();
          const rows = j?.value || j?.data || j?.Data || (Array.isArray(j) ? j : []);
          return rows.filter((r) => (r?.id ?? r?.Id) != null);
        } catch { return []; }
      };
      const [users, userGroups] = await Promise.all([
        pull('/api/Users?$select=Id,FirstName,LastName,Email&$orderby=FirstName&$top=5000'),
        pull('/api/user-groups?$orderby=Code&$top=1000'),
      ]);
      if (!cancelled) setLookups({ users, userGroups });
    })();
    return () => { cancelled = true; };
  }, []);

  // Setting satırını upsert'ler. previous: hata halinde geri alınacak eski değer.
  const persist = async (p, value, previous) => {
    setSavingKey(p.key);
    setValues((v) => ({ ...v, [p.key]: value })); // iyimser
    try {
      // Çoklu seçim DB'de virgülle ayrılmış TEK string olarak saklanır (ayrı tablo açmamak için).
      // Dizi olan state'i burada düzleştiriyoruz; values[] dizi kalmalı ki onay kutuları çalışsın.
      const wire = Array.isArray(value) ? value.join(',') : String(value);
      const res = await fetch('/api/settings/SaveByKey', {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ key: p.key, value: wire, type: p.type, group: p.group, order: 0 }),
      });
      if (!res.ok) throw new Error('Kaydedilemedi');
      setSaved((sv) => ({ ...sv, [p.key]: value }));
      showToast('success', 'Parametre kaydedildi');
    } catch (e) {
      setValues((v) => ({ ...v, [p.key]: previous })); // geri al
      showToast('error', e.message || 'Kayıt hatası');
    } finally {
      setSavingKey(null);
    }
  };

  // Sayısal parametreyi kaydet — min/max sınırlarına çek.
  const saveNumber = (p) => {
    let n = Number(values[p.key]);
    if (!Number.isFinite(n)) n = p.default;
    if (p.min != null && n < p.min) n = p.min;
    if (p.max != null && n > p.max) n = p.max;
    persist(p, n, saved[p.key] ?? p.default);
  };

  // Metin / saat / çoklu seçim kaydı. Diziyi düzleştirme işi persist() içindedir; buraya
  // ham değeri veriyoruz ki state dizi kalsın ve onay kutuları çalışmaya devam etsin.
  const saveValue = (p) => persist(p, values[p.key] ?? p.default, saved[p.key] ?? p.default);

  const asText = (v) => (Array.isArray(v) ? v.join(',') : String(v ?? ''));
  const isDirty = (p) => asText(values[p.key]) !== asText(saved[p.key]);

  // Sunucudan gelen seçenek kaynağı: hangi liste ve hangi rehber (relation) config'i?
  // dependsOn verilmişse kaynak o parametrenin o anki değerine göre değişir (Kod ↔ Grup).
  const sourceFor = (p) => {
    const key = typeof p.optionsSource === 'function' ? p.optionsSource(values[p.dependsOn]) : p.optionsSource;
    return {
      key,
      rows: lookups[key] || [],
      config: key === 'userGroups' ? RELATION_CONFIGS.userGroup : RELATION_CONFIGS.user,
    };
  };

  // multiselect seçenekleri: statik options ya da sunucu kaynağının {value,label}'a indirgenmiş hâli.
  const optionsFor = (p) => {
    if (p.options) return p.options;
    const { rows, config } = sourceFor(p);
    return rows.map((r) => ({ value: String(r.id ?? r.Id), label: config.getRowLabel(r) }));
  };

  const toggleMulti = (p, optValue) => {
    setValues((v) => {
      const cur = Array.isArray(v[p.key]) ? v[p.key] : [];
      const next = cur.includes(optValue) ? cur.filter((x) => x !== optValue) : [...cur, optValue];
      return { ...v, [p.key]: next };
    });
  };

  const renderControl = (p) => {
    if (p.type === 'bool') {
      const on = !!values[p.key];
      return (
        <label className="gp-switch">
          <input
            type="checkbox"
            checked={on}
            disabled={savingKey === p.key || !canSave}
            onChange={(e) => persist(p, e.target.checked, !e.target.checked)}
          />
          <span className="gp-switch-track"><span className="gp-switch-thumb" /></span>
          <span className="gp-switch-txt">{on ? 'Açık' : 'Kapalı'}</span>
        </label>
      );
    }
    if (p.type === 'number') {
      return (
        <>
          <span className="gp-num">
            <input
              type="number"
              min={p.min}
              max={p.max}
              value={values[p.key] ?? ''}
              disabled={savingKey === p.key}
              onChange={(e) => setValues((v) => ({ ...v, [p.key]: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter' && isDirty(p)) saveNumber(p); }}
            />
            {p.unit && <span className="gp-num-unit">{p.unit}</span>}
          </span>
          <button
            type="button"
            className="dps-btn primary gp-save"
            onClick={() => saveNumber(p)}
            disabled={savingKey === p.key || !isDirty(p) || !canSave}
          >
            {savingKey === p.key ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
        </>
      );
    }
    // Tek seçim — anahtar gibi ANINDA kaydeder; ayrı Kaydet butonu gereksiz tıklama olurdu.
    if (p.type === 'select') {
      return (
        <select
          className="gp-select"
          value={values[p.key] ?? p.default}
          disabled={savingKey === p.key || !canSave}
          onChange={(e) => persist(p, e.target.value, saved[p.key] ?? p.default)}
        >
          {p.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }
    if (p.type === 'time' || p.type === 'text') {
      return (
        <>
          <span className="gp-num">
            <input
              type={p.type === 'time' ? 'time' : 'text'}
              placeholder={p.placeholder}
              value={values[p.key] ?? ''}
              disabled={savingKey === p.key}
              onChange={(e) => setValues((v) => ({ ...v, [p.key]: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter' && isDirty(p)) saveValue(p); }}
            />
          </span>
          <button
            type="button"
            className="dps-btn primary gp-save"
            onClick={() => saveValue(p)}
            disabled={savingKey === p.key || !isDirty(p) || !canSave}
          >
            {savingKey === p.key ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
        </>
      );
    }
    // #817 Rehberli çoklu seçim — arama/sıralama/sayfalama datagrid'i + chip'ler + Tümünü Seç/Temizle.
    if (p.type === 'multiselect' && p.picker) {
      const { key: sourceKey, rows, config } = sourceFor(p);
      const selected = Array.isArray(values[p.key]) ? values[p.key] : [];
      const rowById = new Map(rows.map((r) => [String(r.id ?? r.Id), r]));
      // Listede bulunmayan id (silinmiş kullanıcı / başka tenant kaydı) ATILMAZ; "#12" olarak
      // gösterilir ki kaydedilmiş kriter kullanıcı farkında olmadan değişmesin.
      const items = selected.map((v) => {
        const row = rowById.get(String(v));
        return { value: String(v), label: row ? config.getRowLabel(row) : `#${v}` };
      });
      const allItems = rows.map((r) => ({ value: String(r.id ?? r.Id), label: config.getRowLabel(r) }));
      const isGroup = sourceKey === 'userGroups';
      // "Boş = herkes" konvansiyonu (LoginSecurityService.CheckLoginWindowAsync): kriter boşken
      // kısıt HERKESE uygulanır. Kısıt açıkken bunu sessizce bırakmak kilitlenme riskidir.
      const warnEveryone = selected.length === 0 && !!values[SETTING_LOGIN_WINDOW_ENABLED];
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', minWidth: 0 }}>
          <MultiChipSelect
            items={items}
            allItems={allItems}
            rows={rows}
            config={config}
            placeholder={isGroup ? 'Kullanıcı grubu ara ve ekle…' : 'Kullanıcı ara ve ekle…'}
            disabled={savingKey === p.key || !canSave}
            onChange={(next) => setValues((v) => ({ ...v, [p.key]: next.map((x) => String(x.value)) }))}
          />
          {warnEveryone && (
            <div className="gp-warn">
              Kriter boş — kısıt <b>TÜM kullanıcılara</b> uygulanacak.
            </div>
          )}
          <div>
            <button
              type="button"
              className="dps-btn primary gp-save"
              onClick={() => saveValue(p)}
              disabled={savingKey === p.key || !isDirty(p) || !canSave}
            >
              {savingKey === p.key ? 'Kaydediliyor…' : 'Kaydet'}
            </button>
          </div>
        </div>
      );
    }
    if (p.type === 'multiselect') {
      const opts = optionsFor(p);
      const selected = Array.isArray(values[p.key]) ? values[p.key] : [];
      return (
        <>
          <div className="gp-multi">
            {opts.length === 0
              ? <span className="gp-multi-empty">Seçenek yok</span>
              : opts.map((o) => (
                <label key={o.value} className={`gp-chip${selected.includes(o.value) ? ' on' : ''}`}>
                  <input
                    type="checkbox"
                    checked={selected.includes(o.value)}
                    disabled={savingKey === p.key || !canSave}
                    onChange={() => toggleMulti(p, o.value)}
                  />
                  <span>{o.label}</span>
                </label>
              ))}
          </div>
          <button
            type="button"
            className="dps-btn primary gp-save"
            onClick={() => saveValue(p)}
            disabled={savingKey === p.key || !isDirty(p) || !canSave}
          >
            {savingKey === p.key ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
        </>
      );
    }
    return null;
  };

  return (
    <DetailShell
      crumb={[{ label: 'Genel Parametreler', current: true }]}
      loading={loading}
    >
      <div className="dps-form">
        {GROUPS.map((grp) => (
          <Section key={grp.name} icon={grp.icon} title={grp.name} subtitle={grp.subtitle}>
            <div className="gp-grid">
              {grp.items.map((item) => {
                const p = paramByKey[item.key];
                return (
                  <div key={p.key} className="gp-card">
                    <div className="gp-card-info">
                      <div className="gp-card-label">{p.label}</div>
                      {p.help && <div className="gp-card-help">{p.help}</div>}
                    </div>
                    <div className="gp-card-ctrl">{renderControl(p)}</div>
                  </div>
                );
              })}
            </div>
          </Section>
        ))}
      </div>
      <Toast toast={toast} />
    </DetailShell>
  );
}

export default GeneralParametersPage;
